import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Loader2, Plus, Search, X } from 'lucide-react';
import { apiFetch, readApiErrorMessage } from '../lib/apiFetch';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { formatGroupLabel } from '../utils/groupLabel';
import { readGroupMembershipCounts, type GroupMembershipCounts } from '../utils/groupMembership';
import TimeAgo from './TimeAgo';

const SEARCH_DEBOUNCE_MS = 300;

interface GroupSummary {
  id: string;
  type: 'manual';
  title: string;
  record_count: number;
  created: string | null;
}

function compareGroupsByCreatedDesc(a: GroupSummary, b: GroupSummary): number {
  const aCreated = a.created ? Date.parse(a.created) : Number.NaN;
  const bCreated = b.created ? Date.parse(b.created) : Number.NaN;
  const aTime = Number.isNaN(aCreated) ? 0 : aCreated;
  const bTime = Number.isNaN(bCreated) ? 0 : bCreated;
  return bTime - aTime;
}

interface AddToGroupModalProps {
  /** Records being added to a group. */
  recordIds: string[];
  /** Group ids shown as already added. Pass an empty list when membership is not known for every record. */
  existingGroupIds: string[];
  onClose: () => void;
  /** Called once the request succeeds, including when every record was already a member. */
  onAdded: (group: GroupSummary, counts: GroupMembershipCounts) => void;
}

export default function AddToGroupModal({
  recordIds,
  existingGroupIds,
  onClose,
  onAdded,
}: AddToGroupModalProps) {
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  // True only until the first fetch resolves; later fetches keep the list visible (see isSearching).
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const [isAdding, setIsAdding] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const busy = isCreating || isAdding;
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, {
    onEscape: () => {
      if (!busy) onClose();
    },
  });

  const requestIdRef = useRef(0);

  const fetchGroups = useCallback(async (search: string) => {
    const requestId = ++requestIdRef.current;
    setIsSearching(true);
    try {
      const query = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
      const response = await apiFetch(`/api/v1/groups${query}`);
      if (!response.ok) {
        throw new Error(await readApiErrorMessage(response, 'Failed to load groups'));
      }
      const data = await response.json();
      if (requestId !== requestIdRef.current) return; // superseded by a later request
      const loaded: GroupSummary[] = data._embedded?.groups || [];
      const sorted = loaded.slice().sort(compareGroupsByCreatedDesc);
      setGroups(sorted);
      setErrorMessage(null);
      // The previously selected group may have scrolled out of this result set.
      setSelectedGroupId((prev) => (prev && sorted.some((g) => g.id === prev) ? prev : null));
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load groups.');
    } finally {
      if (requestId === requestIdRef.current) {
        setIsSearching(false);
        setIsInitialLoading(false);
      }
    }
  }, []);

  // Debounce search-as-you-type; the initial empty-term load fires immediately.
  useEffect(() => {
    const timer = setTimeout(
      () => fetchGroups(searchTerm),
      searchTerm === '' ? 0 : SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchTerm, fetchGroups]);

  const createInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isCreatingNew) createInputRef.current?.focus();
  }, [isCreatingNew]);

  const handleCreateGroup = async () => {
    const title = newGroupTitle.trim();
    if (!title || isCreating) return;
    setIsCreating(true);
    setErrorMessage(null);
    try {
      const response = await apiFetch('/api/v1/groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (!response.ok) {
        throw new Error(await readApiErrorMessage(response, 'Failed to create group'));
      }
      const data = await response.json();
      const newGroup: GroupSummary | undefined = data._embedded?.group;
      if (!newGroup) throw new Error('Failed to create group');

      // Invalidate any in-flight search so its response can't land after this
      // and wipe the group just created out of the list.
      requestIdRef.current += 1;
      setIsSearching(false);
      setGroups((prev) => [newGroup, ...prev].sort(compareGroupsByCreatedDesc));
      setSelectedGroupId(newGroup.id);
      setIsCreatingNew(false);
      setNewGroupTitle('');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to create group.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleAdd = async () => {
    const selectedGroup = groups.find((g) => g.id === selectedGroupId);
    if (!selectedGroup || isAdding) return;
    setIsAdding(true);
    setErrorMessage(null);
    try {
      const response = await apiFetch(`/api/v1/groups/${selectedGroup.id}/records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ record_ids: recordIds }),
      });
      if (!response.ok) {
        throw new Error(
          await readApiErrorMessage(
            response,
            recordIds.length === 1 ? 'Failed to add record to group' : 'Failed to add records to group',
          ),
        );
      }
      onAdded(selectedGroup, readGroupMembershipCounts(await response.json()));
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to add record to group.');
      setIsAdding(false);
    }
  };

  let addButtonLabel = 'Add';
  if (isAdding) addButtonLabel = 'Adding...';
  else if (recordIds.length > 1) addButtonLabel = `Add ${recordIds.length}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={() => !busy && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-to-group-title"
        tabIndex={-1}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 flex flex-col h-[640px] max-h-[80vh] outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4 shrink-0">
          <div className="min-w-0">
            <h3 id="add-to-group-title" className="text-lg font-bold text-gray-900">
              Add to Group
            </h3>
            {recordIds.length > 1 && (
              <p className="text-sm text-gray-500 mt-1">
                {recordIds.length} records
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setIsCreatingNew(true)}
              disabled={busy || isCreatingNew}
              aria-label="Create new group"
              title="Create new group"
              className="p-2 rounded-lg text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label="Close"
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 pb-4 shrink-0">
          <div className="relative">
            {isSearching && !isInitialLoading ? (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
            ) : (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            )}
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
              autoFocus
            />
          </div>
        </div>

        {/* Inline "create new group" row */}
        {isCreatingNew && (
          <div className="px-6 pb-3 shrink-0">
            <div className="flex items-center gap-2 p-2.5 rounded-xl border-2 border-emerald-300 bg-emerald-50/50">
              <input
                ref={createInputRef}
                type="text"
                value={newGroupTitle}
                onChange={(e) => setNewGroupTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateGroup();
                  if (e.key === 'Escape') {
                    setIsCreatingNew(false);
                    setNewGroupTitle('');
                  }
                }}
                placeholder="New group name"
                maxLength={200}
                disabled={isCreating}
                className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:opacity-50"
              />
              <button
                type="button"
                onClick={handleCreateGroup}
                disabled={isCreating || !newGroupTitle.trim()}
                aria-label="Confirm new group"
                className="p-1.5 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsCreatingNew(false);
                  setNewGroupTitle('');
                }}
                disabled={isCreating}
                aria-label="Cancel new group"
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mx-6 mb-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shrink-0">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Group list */}
        <div className="px-4 pb-2 flex-1 overflow-y-auto min-h-[200px]">
          {isInitialLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading groups...
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm px-4">
              {searchTerm ? 'No matching groups.' : "No groups yet — create one with the + button above."}
            </div>
          ) : (
            <div className="space-y-1">
              {groups.map((group) => {
                const alreadyMember = existingGroupIds.includes(group.id);
                const isSelected = selectedGroupId === group.id;
                return (
                  <button
                    key={group.id}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    disabled={alreadyMember || busy}
                    onClick={() => setSelectedGroupId(group.id)}
                    className={`w-full text-left p-3 rounded-xl border transition-colors flex items-center justify-between gap-3 ${
                      alreadyMember
                        ? 'border-transparent bg-gray-50 opacity-60 cursor-not-allowed'
                        : isSelected
                        ? 'border-emerald-300 bg-emerald-50'
                        : 'border-transparent hover:bg-gray-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    <div className="min-w-0 flex items-center gap-3">
                      <div
                        className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                          isSelected ? 'border-emerald-600' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <div className="w-2 h-2 rounded-full bg-emerald-600" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {formatGroupLabel(group)}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {group.record_count} record{group.record_count !== 1 ? 's' : ''}
                          {group.created && (
                            <>
                              {' · '}
                              <TimeAgo date={group.created} />
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    {alreadyMember && (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 shrink-0">
                        Already added
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-4 shrink-0">
          <button
            type="button"
            onClick={handleAdd}
            disabled={!selectedGroupId || busy}
            className="px-6 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg shadow-emerald-200 flex items-center transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:transform-none bg-emerald-800 hover:bg-emerald-700"
          >
            {isAdding && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {addButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
